/**
 * Authentication Guard Components
 * Provides route protection and conditional rendering based on authentication state
 */

'use client';

import React, { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { PageSpinner } from '@UIKit';
import { ROUTES } from '@Constants/navigation.contants';
import { useAuth } from '../providers/AuthProvider';
import { AuthGuardProps } from '../types';

/**
 * Private Route Guard
 * Protects routes that require authentication
 * Redirects to login page if user is not authenticated
 */
export const PrivateRouteGuard: React.FC<AuthGuardProps> = ({
    children,
    fallback,
    redirectTo = ROUTES.LOGIN
}) => {
    const { isAuthenticated, isLoading } = useAuth();
    const router = useRouter();

    useEffect(() => {
    // Only redirect after loading is complete and user is not authenticated
        if (!isLoading && !isAuthenticated) {
            router.replace(redirectTo);
        }
    }, [isAuthenticated, isLoading, router, redirectTo]);

    // Show loading state while checking authentication
    if (isLoading) {
        return fallback || <PageSpinner />;
    }

    // Show nothing while redirecting
    if (!isAuthenticated) {
        return null;
    }

    // User is authenticated, render children
    return <>{children}</>;
};

/**
 * Public Route Guard
 * Protects routes that should only be accessible to non-authenticated users
 * Redirects to dashboard if user is already authenticated
 */
export const PublicRouteGuard: React.FC<AuthGuardProps> = ({
    children,
    fallback,
    redirectTo = '/'
}) => {
    const { isAuthenticated, isLoading } = useAuth();
    const router = useRouter();

    useEffect(() => {
    // Only redirect after loading is complete and user is authenticated
        if (!isLoading && isAuthenticated) {
            router.replace(redirectTo);
        }
    }, [isAuthenticated, isLoading, router, redirectTo]);

    // Show loading state while checking authentication
    if (isLoading) {
        return fallback || <PageSpinner />;
    }

    // Show nothing while redirecting
    if (isAuthenticated) {
        return null;
    }

    // User is not authenticated, render children
    return <>{children}</>;
};

/**
 * Conditional Authentication Guard
 * Renders different content based on authentication state
 */
interface ConditionalAuthGuardProps {
  authenticated: React.ReactNode;
  unauthenticated: React.ReactNode;
  loading?: React.ReactNode;
}

export const ConditionalAuthGuard: React.FC<ConditionalAuthGuardProps> = ({
    authenticated,
    unauthenticated,
    loading
}) => {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
        return <>{loading || <PageSpinner />}</>;
    }

    return <>{isAuthenticated ? authenticated : unauthenticated}</>;
};

/**
 * Higher-Order Component for private routes
 */
export function withPrivateRoute<P extends object>(
    Component: React.ComponentType<P>,
    options: {
    fallback?: React.ReactNode;
    redirectTo?: string;
  } = {}
) {
    const WrappedComponent = (props: P) => (
        <PrivateRouteGuard {...options}>
            <Component {...props} />
        </PrivateRouteGuard>
    );

    WrappedComponent.displayName = `withPrivateRoute(${Component.displayName || Component.name})`;
    return WrappedComponent;
}

/**
 * Higher-Order Component for public routes
 */
export function withPublicRoute<P extends object>(
    Component: React.ComponentType<P>,
    options: {
    fallback?: React.ReactNode;
    redirectTo?: string;
  } = {}
) {
    const WrappedComponent = (props: P) => (
        <PublicRouteGuard {...options}>
            <Component {...props} />
        </PublicRouteGuard>
    );

    WrappedComponent.displayName = `withPublicRoute(${Component.displayName || Component.name})`;
    return WrappedComponent;
}
